import { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  Button,
  HorizontalCard,
  ProgressWithLabel,
  Spacing,
  TbdPlayer,
  Typography,
  Audio,
  ImageContainer,
  RichText,
  PdfViewer,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import {
  DownloadStatus,
  ITraining,
  downloadManagerService,
  MediaType,
} from '@taleemabad/db';
import { css } from '@emotion/react';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';

interface TrainingVideoProps {
  training: ITraining;
  trainingScoreText: string;
  isAPILoading: boolean;
  buttonClickHandler: () => void;
}

export const TrainingVideo: FC<TrainingVideoProps> = ({
  training,
  trainingScoreText,
  isAPILoading,
  buttonClickHandler,
}) => {
  const [mediaFinished, setMediaFinished] = useState<boolean>(false);
  const [autoplay, setAutoplay] = useState<boolean>(true);
  const [isDownloadUrlAvailable, setIsDownloadUrlAvailable] =
    useState<boolean>(false);
  const [url, setUrl] = useState<string | null>(null);
  const getDownloadUrl = async (url: string) => {
    const downloadUrl = await downloadManagerService.getDownloadURL(url);
    if (
      downloadUrl &&
      Capacitor.isNativePlatform() &&
      !(await Network.getStatus()).connected
    ) {
      setIsDownloadUrlAvailable(true);
      setUrl(downloadUrl);
    } else {
      if (training.mediaAsset) setUrl(training.mediaAsset.url);
    }
  };

  useEffect(() => {
    if (training.mediaAsset) getDownloadUrl(training.mediaAsset.url);
  }, [training]);
  const getButtonProps = () => {
    const isRetake = training?.assessment;
    const isMediaTraining = training?.mediaAsset;
    if (isMediaTraining && training.mediaAsset?.type === MediaType.Pdf) {
      return {
        text: 'Start Quiz',
        disabled: false,
      };
    }
    if (!isMediaTraining) {
      return {
        text: 'Start Quiz',
        disabled: false,
      };
    }
    return {
      text: isRetake ? 'Retake Quiz' : 'Start Quiz',
      disabled: !isRetake && !mediaFinished,
    };
  };
  const { text, disabled } = getButtonProps();

  const handleVideoEnd = () => {
    setAutoplay(false);
    setMediaFinished(true);
  };
  const handleAudioEnd = () => {
    setMediaFinished(true);
  };

  const calculateScorePercentage = () => {
    if (!training.assessment) {
      return 0;
    }
    return (training.assessment?.score / training.assessment?.totalScore) * 100;
  };
  return (
    <>
      <TrainingVideoHeaderContainer>
        <HeadlineTextWrapper>
          <Typography
            data-cy="teacher-training-training-video-title"
            variant="textHeadlineMediumOnSurface"
          >
            {training.title}
          </Typography>
        </HeadlineTextWrapper>
        {training.assessment && (
          <HorizontalCard
            variant="filled"
            maxWidth={356}
            title="Quiz Score"
            titleContentCss={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
            `}
            tailComponent={
              <ProgressWithLabel
                value={calculateScorePercentage()}
                text={trainingScoreText}
              />
            }
          />
        )}
      </TrainingVideoHeaderContainer>
      {training.content && <RichText htmlContent={training.content} />}
      {training.mediaAsset?.type === MediaType.Video && (
        <TrainingVideoWrapper>
          <TbdPlayer
            url={url ?? ''}
            hlsUrl={
              !isDownloadUrlAvailable
                ? training.mediaAsset.hlsVideoMainManifestUrl
                : null
            }
            featureName="training"
            onVideoEnd={handleVideoEnd}
            autoplay={autoplay}
            eventData={{
              epMediaId: training.mediaAsset.id,
              epCourseId: training.course,
              epTrainingId: training.id,
              epIsDownload:
                training.mediaAsset?.download?.status ===
                DownloadStatus.Completed,
            }}
          />
        </TrainingVideoWrapper>
      )}
      {training.mediaAsset?.type === MediaType.Audio && (
        <Audio
          isPlaying={true}
          src={training.mediaAsset.url}
          playbackOption={true}
          onAudioEnd={handleAudioEnd}
        />
      )}
      {training.mediaAsset?.type === MediaType.Image && (
        <ImageContainer
          src={training.mediaAsset.url}
          alt={training.mediaAsset.name}
          width="500px"
          height="500px"
        />
      )}
      {training.mediaAsset?.type === MediaType.Pdf && (
        <Spacing padding="xxxl">
          <PdfViewer
            url={training.mediaAsset.url}
            fileName={training.mediaAsset.name}
          />
        </Spacing>
      )}
      <Spacing marginLeft="xl">
        <Spacing margin="md none">
          <Typography
            data-cy="teacher-training-video-overview"
            variant="textTitleMediumNeutral10"
          >
            Overview
          </Typography>
        </Spacing>
        <Spacing marginBottom="xl">
          <Typography
            data-cy="teacher-training-video-description"
            variant="textTitleMediumNeutral50"
          >
            {training.description}
          </Typography>
        </Spacing>
        {training?.assessment && (
          <Spacing marginBottom="xxl">
            <Typography
              data-cy="teacher-training-video-description"
              variant="textBodyLargeSecondary30"
            >
              Your quiz is complete. <br />
              Retake quiz to improve your score.
            </Typography>
          </Spacing>
        )}
        <Button
          data-cy="teacher-training-quiz-button"
          onClick={buttonClickHandler}
          disabled={disabled || isAPILoading}
          variant={training?.assessment ? 'outlined' : 'filled'}
        >
          {text}
        </Button>
        <Spacing marginBottom="xxl" />
      </Spacing>
    </>
  );
};

const TrainingVideoHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 var(--rk-spacing-xl);

  ${mq(BreakpointSizes.mobile)} {
    justify-content: flex-start;
  }
`;

const HeadlineTextWrapper = styled.div`
  ${mq(BreakpointSizes.mobile)} {
  }
`;

const TrainingVideoWrapper = styled.div`
  padding: var(--rk-spacing-lg);
  margin: var(--rk-spacing-xl) 0;
  ${mq(BreakpointSizes.mobile)} {
    padding: unset;
  }
`;
